import {
  Accordion,
  AccordionDetails,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { memo, useEffect } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { FormikProps } from 'formik';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import PmiTextField from 'shell/components/shared/text-field';
import { FiltersFormTerritorialArea } from 'system/pages/toponymy/territorial-area/list/types';
import AddressAutoComplete from 'system/components/shared/search-address';
import { initialValuesTerritorialAreas } from 'system/pages/toponymy/territorial-area';

interface FiltersTerritorialAreaProps {
  filterTerritorialAreaForm: FormikProps<FiltersFormTerritorialArea>;
  setOpen: (value: boolean) => void;
  open?: boolean;
}

const FiltersTerritorialAreaComponent = ({
  filterTerritorialAreaForm,
  setOpen,
  open,
}: FiltersTerritorialAreaProps): React.ReactElement => {
  useEffect(() => {
    if (!filterTerritorialAreaForm.values.civicNumberFrom) {
      filterTerritorialAreaForm.setFieldValue('exponentFrom', '');
    }
  }, [filterTerritorialAreaForm.values.civicNumberFrom]);

  useEffect(() => {
    if (!filterTerritorialAreaForm.values.civicNumberTo) {
      filterTerritorialAreaForm.setFieldValue('exponentTo', '');
    }
  }, [filterTerritorialAreaForm.values.civicNumberTo]);

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-territorialarea-content"
        id="panel-territorialarea-header"
        classes={{
          root: 'pmi-flex-row-reverse',
        }}
        expandIcon={<ExpandMore />}
      >
        <Typography>Filtri</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <form
          onSubmit={filterTerritorialAreaForm.handleSubmit}
          onReset={filterTerritorialAreaForm.handleReset}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <AddressAutoComplete
                aria-label="Via"
                label="Via"
                id="streetName"
                required
                selectedValue={filterTerritorialAreaForm.values.streetName}
                handleChange={(value) =>
                  filterTerritorialAreaForm.setFieldValue('streetName', value)
                }
                error={
                  filterTerritorialAreaForm.touched.streetName &&
                  Boolean(filterTerritorialAreaForm.errors.streetName)
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <PmiTextField
                aria-label="Codice via"
                label="Codice via"
                id="streetCode"
                name="streetCode"
                fullWidth
                type="text"
                variant="outlined"
                value={filterTerritorialAreaForm.values.streetName?.code || ''}
                disabled
                error={
                  filterTerritorialAreaForm.touched.streetCode &&
                  Boolean(filterTerritorialAreaForm.errors.streetCode)
                }
                helperText={filterTerritorialAreaForm.errors.streetCode}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <PmiTextField
                aria-label="Civico da"
                label="Civico da"
                id="civicNumberFrom"
                name="civicNumberFrom"
                fullWidth
                type="text"
                variant="outlined"
                value={filterTerritorialAreaForm.values.civicNumberFrom}
                onChange={filterTerritorialAreaForm.handleChange}
                error={
                  filterTerritorialAreaForm.touched.civicNumberFrom &&
                  Boolean(filterTerritorialAreaForm.errors.civicNumberFrom)
                }
                helperText={filterTerritorialAreaForm.errors.civicNumberFrom}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <PmiTextField
                aria-label="Esponente da"
                label="Esponente da"
                id="exponentFrom"
                name="exponentFrom"
                fullWidth
                type="text"
                variant="outlined"
                disabled={!filterTerritorialAreaForm.values.civicNumberFrom}
                value={filterTerritorialAreaForm.values.exponentFrom}
                onChange={filterTerritorialAreaForm.handleChange}
                error={
                  filterTerritorialAreaForm.touched.exponentFrom &&
                  Boolean(filterTerritorialAreaForm.errors.exponentFrom)
                }
                helperText={filterTerritorialAreaForm.errors.exponentFrom}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <PmiTextField
                aria-label="Civico a"
                label="Civico a"
                id="civicNumberTo"
                name="civicNumberTo"
                fullWidth
                type="text"
                variant="outlined"
                value={filterTerritorialAreaForm.values.civicNumberTo}
                onChange={filterTerritorialAreaForm.handleChange}
                error={
                  filterTerritorialAreaForm.touched.civicNumberTo &&
                  Boolean(filterTerritorialAreaForm.errors.civicNumberTo)
                }
                helperText={filterTerritorialAreaForm.errors.civicNumberTo}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <PmiTextField
                aria-label="Esponente a"
                label="Esponente a"
                id="exponentTo"
                name="exponentTo"
                fullWidth
                type="text"
                variant="outlined"
                disabled={!filterTerritorialAreaForm.values.civicNumberTo}
                value={filterTerritorialAreaForm.values.exponentTo}
                onChange={filterTerritorialAreaForm.handleChange}
                error={
                  filterTerritorialAreaForm.touched.exponentTo &&
                  Boolean(filterTerritorialAreaForm.errors.exponentTo)
                }
                helperText={filterTerritorialAreaForm.errors.exponentTo}
              />
            </Grid>

            <Grid item xs={12} md={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Tipologia civici</FormLabel>
                <RadioGroup
                  row
                  name="civicType"
                  value={filterTerritorialAreaForm.values.civicType}
                  onChange={filterTerritorialAreaForm.handleChange}
                >
                  <FormControlLabel value="all" control={<Radio />} label="Tutti i civici" />
                  <FormControlLabel value="odd" control={<Radio />} label="Solo civici dispari" />
                  <FormControlLabel value="even" control={<Radio />} label="Solo civici pari" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item container spacing={1} xs={12} justifyContent="flex-end" display="flex">
              <Grid item xs={12} md={2}>
                <PmiButton
                  variant="outlined"
                  fullWidth
                  color="cdmBrand"
                  onClick={() =>
                    filterTerritorialAreaForm.resetForm({ values: initialValuesTerritorialAreas })
                  }
                >
                  Ripulisci
                </PmiButton>
              </Grid>
              <Grid item xs={12} md={2}>
                <PmiButton variant="contained" color="secondary" type="submit" fullWidth>
                  Cerca
                </PmiButton>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </AccordionDetails>
    </Accordion>
  );
};

FiltersTerritorialAreaComponent.displayName = 'Filters Territorial Area component';

const FiltersTerritorialArea = memo(FiltersTerritorialAreaComponent);

export default FiltersTerritorialArea;
